import { useEffect, useMemo, useState } from "react";
import { Check, Search, Trash2, TriangleAlert } from "lucide-react";
import api from "@/api/axios";

interface Group {
  chat_id: string;
  group_name: string;
  consecutive_failures?: number;
}

type Props = {
  groups: Group[];
  selectedGroups: string[];
  refresh: () => void;
  preview?: boolean;
};

export default function GroupSection({ groups, selectedGroups, refresh, preview = false }: Props) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set(selectedGroups.map(String)));
  const [batchIds, setBatchIds] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState("");
  const [batchMode, setBatchMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const savedIds = useMemo(() => new Set(selectedGroups.map(String)), [selectedGroups]);
  const changed = selectedIds.size !== savedIds.size || Array.from(selectedIds).some((id) => !savedIds.has(id));

  useEffect(() => {
    setSelectedIds(new Set(selectedGroups.map(String)));
  }, [selectedGroups]);

  const filteredGroups = groups.filter((group) =>
    group.group_name.toLocaleLowerCase().includes(filter.trim().toLocaleLowerCase()) &&
    (!batchMode || savedIds.has(String(group.chat_id)))
  );

  const toggleGroup = (id: string) => {
    setError("");
    const next = new Set(batchMode ? batchIds : selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    if (batchMode) setBatchIds(next);
    else setSelectedIds(next);
  };

  const save = async () => {
    if (preview) {
      setError("这是界面预览，群组选择不会保存。");
      return;
    }
    if (selectedIds.size === 0) {
      setError("请至少选择一个打卡群组。");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const selected = groups.filter((group) => selectedIds.has(String(group.chat_id))).map(({ chat_id, group_name }) => ({ chat_id, group_name }));
      const response = await api.post("/api/save_user_groups", { groups: selected });
      if (!response.data.success) throw new Error("保存失败");
      refresh();
    } catch {
      setError("保存失败，请稍后重试。");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (preview) {
      setError("这是界面预览，群组不会删除。");
      return;
    }
    if (!batchIds.size || !window.confirm(`确定从打卡列表中删除选中的 ${batchIds.size} 个群组吗？`)) return;
    setSaving(true);
    setError("");
    try {
      const response = await api.post("/api/batch_delete_user_groups", { chat_ids: Array.from(batchIds) });
      if (!response.data.success) throw new Error("删除失败");
      setSelectedIds((current) => new Set(Array.from(current).filter((id) => !batchIds.has(id))));
      setBatchIds(new Set());
      setBatchMode(false);
      refresh();
    } catch {
      setError("删除失败，请稍后重试。");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="user-config-section">
      <div className="user-config-toolbar">
        <div><h3>打卡群组</h3><p>已保存 {savedIds.size} 个群组</p></div>
        <button className={`user-button ${batchMode ? "user-button-secondary" : "user-button-danger-quiet"}`} type="button" onClick={() => { setBatchMode(!batchMode); setBatchIds(new Set()); setError(""); }}>
          <Trash2 size={15} />{batchMode ? "退出删除" : "批量删除"}
        </button>
      </div>
      <label className="user-config-search"><Search size={17} /><span className="sr-only">搜索群组</span><input value={filter} onChange={(event) => setFilter(event.target.value)} placeholder="搜索群组名称" /></label>
      {batchMode && <p className="user-config-hint">仅显示已保存的群组。选中后点击下方按钮删除。</p>}
      <div className="user-group-grid">
        {filteredGroups.map((group) => {
          const id = String(group.chat_id);
          const selected = batchMode ? batchIds.has(id) : selectedIds.has(id);
          const failureCount = Number(group.consecutive_failures) || 0;
          return <div key={id} className="user-group-card">
            <button type="button" aria-pressed={selected} title={group.group_name} className={`user-group-item ${selected ? "is-selected" : ""} ${batchMode ? "is-delete-mode" : ""} ${failureCount >= 2 ? "has-warning" : ""}`} onClick={() => toggleGroup(id)}>
              <span className="user-group-check">{selected && <Check size={14} />}</span><span className="user-group-name">{group.group_name}</span>
            </button>
            {failureCount >= 2 && <details className="user-group-warning">
              <summary aria-label={`${group.group_name}连续失败 ${failureCount} 次，查看警示`} title="查看群组异常提示"><TriangleAlert size={17} strokeWidth={2.2} /></summary>
              <span className="user-group-warning-tip" role="status">连续失败 {failureCount} 次，群组可能有问题，请查看打卡记录。</span>
            </details>}
          </div>;
        })}
        {filteredGroups.length === 0 && <p className="user-config-empty">{groups.length === 0 ? "暂时没有可用群组" : "没有匹配的群组"}</p>}
      </div>
      {error && <p className="user-form-error" role="alert">{error}</p>}
      <div className="user-config-footer">
        <span>{batchMode ? `已选 ${batchIds.size} 个待删除群组` : changed ? "群组选择尚未保存" : "当前选择已保存"}</span>
        {batchMode ? <button className="user-button user-button-danger" type="button" disabled={!batchIds.size || saving} onClick={remove}>删除选中群组</button> : <button className="user-button user-button-primary" type="button" disabled={!changed || saving} onClick={save}>{saving ? "保存中…" : "保存打卡群组"}</button>}
      </div>
    </section>
  );
}
